'use client';

import { cn } from '../utils/cn';

const YOUTUBE_EMBED_HOST = 'https://www.youtube-nocookie.com/embed/';

/**
 * Parse a YouTube video ID from a watch / short / embed / youtu.be URL.
 * Returns null for anything that isn't a recognisable YouTube URL — callers
 * can use this both to detect YouTube links and to extract the ID.
 */
export function getYouTubeVideoId(url: string): string | null {
	try {
		const parsed = new URL(url);
		const host = parsed.hostname.replace(/^www\./, '');
		if (host === 'youtu.be') return parsed.pathname.slice(1) || null;
		if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
			if (parsed.pathname === '/watch') return parsed.searchParams.get('v');
			if (parsed.pathname.startsWith('/embed/')) return parsed.pathname.slice('/embed/'.length) || null;
			if (parsed.pathname.startsWith('/shorts/')) return parsed.pathname.slice('/shorts/'.length) || null;
		}
		return null;
	} catch {
		return null;
	}
}

interface YouTubeEmbedProps {
	videoId: string;
	/**
	 * Background mode: muted autoplay loop, no controls or chrome, and
	 * non-interactive (pointer-events disabled so clicks pass through to an
	 * overlay). Defaults to a normal player with controls and autoplay.
	 */
	background?: boolean;
	title?: string;
	/** Applied to the aspect-ratio wrapper — use for sizing/positioning only. */
	className?: string;
}

export function YouTubeEmbed({ videoId, background = false, title = 'Video', className }: YouTubeEmbedProps) {
	const params = new URLSearchParams(
		background
			? {
					autoplay: '1',
					mute: '1',
					controls: '0',
					loop: '1',
					playlist: videoId,
					playsinline: '1',
					modestbranding: '1',
					rel: '0',
					disablekb: '1',
					fs: '0',
					iv_load_policy: '3',
				}
			: {
					autoplay: '1',
					rel: '0',
					modestbranding: '1',
					playsinline: '1',
				},
	);

	return (
		// `max-w-full` + `overflow-hidden` live on the component, not on a class the
		// caller has to remember: the embed is a 16:9 box that must never paint
		// outside its own track, whatever layout it is dropped into.
		<div className={cn('relative aspect-video w-full max-w-full overflow-hidden', className)}>
			<iframe
				src={`${YOUTUBE_EMBED_HOST}${videoId}?${params.toString()}`}
				title={title}
				allow="autoplay; encrypted-media; picture-in-picture; web-share"
				allowFullScreen={!background}
				className={cn('absolute inset-0 h-full w-full', background && 'pointer-events-none')}
			/>
		</div>
	);
}
